import React, { useMemo, useCallback } from 'react';
import { Box, Typography, Button, Alert, Stack, TextField, InputAdornment } from '@mui/material';
import {
  Add as AddIcon,
  Help as HelpIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Page,
  Selection,
  VirtualScroll,
  Sort,
} from '@syncfusion/ej2-react-grids';

import { useAppSelector } from '../../../../../../app/store';
import { useGetCptsByFeeScheduleIdQuery, selectCurrentFeeSchedule } from '../../../../../../services/ContractService';
import { useSignalR } from '../../../../../../hooks/useSignalR';
import { AddEditCptDialog } from '../../../dialogs';
import CptUploadCsv from '../../../commands/contractCommands/CptUploadCsv';
import CptDownloadCsv from '../../../commands/contractCommands/CptDownloadCsv';
import CptHelpDialog from '../../../dialogs/contractDialogs/CptHelpDialog';
import CptUploadProgress from '../../../dialogs/contractDialogs/CptUploadProgress';
import { useCptSearch, useCptDialog, useCptUpload } from './hooks';
import { rateTemplate, weightTemplate, discountTemplate } from './templates';

export interface CptCodesGridProps {
  testId?: string;
}

/**
 * CptCodesGrid - Displays and manages CPT codes for a fee schedule
 *
 * Follows the established grid pattern with:
 * - Separated templates for cell rendering
 * - Custom hooks for search, dialog, and upload handling
 * - SignalR integration for upload progress
 */
export const CptCodesGrid: React.FC<CptCodesGridProps> = ({ testId }) => {
  const selectedFeeSchedule = useAppSelector(selectCurrentFeeSchedule);

  // Fetch CPT codes
  const { data: cpts = [], isLoading, isError, error } = useGetCptsByFeeScheduleIdQuery(
    { feeScheduleId: selectedFeeSchedule?.id || '' },
    { skip: !selectedFeeSchedule?.id }
  );

  // Custom hooks
  const { searchTerm, filteredCpts, handleSearchChange, handleClearSearch } = useCptSearch(cpts);

  const {
    dialogOpen,
    editingCpt,
    helpDialogOpen,
    handleAddCpt,
    handleEditCpt,
    handleCloseDialog,
    handleOpenHelpDialog,
    handleCloseHelpDialog,
  } = useCptDialog();

  const {
    uploadProgress,
    isUploadInProgress,
    onProgress,
    onCompleted,
    onError,
    handleCloseProgressDialog,
    handleStartUpload,
  } = useCptUpload();

  // SignalR connection
  const { isConnected: isSignalRConnected } = useSignalR({
    serviceName: 'Contracts',
    uploadType: 'Cpt',
    onProgress,
    onCompleted,
    onError,
    autoConnect: true,
  });

  // Handle row selection for editing
  const handleRowSelection = useCallback(
    (args: { data?: { id: string } }) => {
      if (args.data) {
        const selectedCpt = filteredCpts.find((c) => c.id === args.data!.id);
        if (selectedCpt) {
          handleEditCpt(selectedCpt);
        }
      }
    },
    [filteredCpts, handleEditCpt]
  );

  // Transform data for grid display
  const gridData = useMemo(() => {
    return filteredCpts
      .map((cpt) => ({
        id: cpt.id,
        code: cpt.code,
        name: cpt.name,
        category: cpt.category,
        apcCode: cpt.apcCode,
        baseRate: cpt.baseRate,
        grouperPointer: cpt.grouperPointer,
        multiProcedureDiscount: cpt.multiProcedureDiscount,
        relativeWeight: cpt.relativeWeight,
      }))
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [filteredCpts]);

  const gridSettings = {
    allowPaging: true,
    allowSorting: true,
    enableVirtualization: true,
    pageSettings: { pageSize: 100, pageSizes: [50, 100, 200, 500] },
    selectionSettings: { type: 'Single' as const, mode: 'Row' as const },
    rowHeight: 32,
  };

  if (!selectedFeeSchedule) {
    return (
      <Alert severity="warning">Please select a fee schedule to view CPT codes.</Alert>
    );
  }

  if (isError) {
    return (
      <Alert severity="error">
        Error loading CPT codes: {error && 'message' in error ? (error as any).message : 'Unknown error'}
      </Alert>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}
        >
          CPT Codes ({filteredCpts.length}{searchTerm && ` of ${cpts.length}`})
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddCpt} size="small">
            Add CPT Code
          </Button>
          <CptUploadCsv
            disabled={isLoading || isUploadInProgress}
            onUploadStart={handleStartUpload}
          />
          <CptDownloadCsv disabled={isLoading || cpts.length === 0} />
          <Button variant="outlined" startIcon={<HelpIcon />} onClick={handleOpenHelpDialog} size="small">
            Help
          </Button>
        </Stack>
      </Stack>

      {/* Search */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <SearchIcon sx={{ color: '#64748b' }} />
          <TextField
            fullWidth
            size="small"
            placeholder="Search by code, name, category, APC code, or grouper..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            InputProps={{
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <ClearIcon
                    sx={{ cursor: 'pointer', color: '#64748b' }}
                    onClick={handleClearSearch}
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f8fafc',
                '&:hover': { backgroundColor: '#f1f5f9' },
              },
            }}
          />
        </Stack>
      </Box>

      {cpts.length > 0 && (
        <Typography
          variant="body2"
          sx={{ color: '#64748b', fontStyle: 'italic', mb: 1, fontSize: '0.75rem' }}
        >
          Click on any row to edit or delete a CPT code
        </Typography>
      )}

      {gridData.length === 0 ? (
        <Alert severity="info">
          {searchTerm
            ? 'No CPT codes match your search criteria.'
            : 'No CPT codes defined yet. Click "Add CPT Code" to create your first CPT code.'}
        </Alert>
      ) : (
        <Box
          sx={{
            border: '1px solid #e2e8f0',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <GridComponent
            dataSource={gridData}
            {...gridSettings}
            height="350"
            width="100%"
            rowSelected={handleRowSelection}
            style={{ border: 'none' }}
            data-testid={testId}
          >
            <ColumnsDirective>
              <ColumnDirective field="code" headerText="Code" width="100" textAlign="Left" />
              <ColumnDirective field="name" headerText="Name" width="200" textAlign="Left" />
              <ColumnDirective field="category" headerText="Category" width="120" textAlign="Left" />
              <ColumnDirective field="apcCode" headerText="APC Code" width="100" textAlign="Left" />
              <ColumnDirective field="grouperPointer" headerText="Grouper" width="100" textAlign="Left" />
              <ColumnDirective
                field="baseRate"
                headerText="Rate"
                width="120"
                textAlign="Right"
                template={rateTemplate}
              />
              <ColumnDirective
                field="relativeWeight"
                headerText="Weight"
                width="100"
                textAlign="Right"
                template={weightTemplate}
              />
              <ColumnDirective
                field="multiProcedureDiscount"
                headerText="Multi-Proc Disc"
                width="120"
                textAlign="Center"
                template={discountTemplate}
              />
            </ColumnsDirective>
            <Inject services={[Page, Selection, VirtualScroll, Sort]} />
          </GridComponent>
        </Box>
      )}

      {/* Dialogs */}
      <AddEditCptDialog
        open={dialogOpen}
        editingCpt={editingCpt}
        feeScheduleId={selectedFeeSchedule?.id || ''}
        onClose={handleCloseDialog}
      />

      <CptHelpDialog open={helpDialogOpen} onClose={handleCloseHelpDialog} />

      {uploadProgress && (
        <CptUploadProgress
          open={!!uploadProgress}
          progress={uploadProgress?.progressPercentage || 0}
          status={uploadProgress?.message || 'Preparing upload...'}
          error={uploadProgress?.status === 'Failed' ? uploadProgress.message : undefined}
          success={uploadProgress?.progressPercentage === 100}
          uploadCount={uploadProgress?.processedRecords}
          totalCount={uploadProgress?.totalRecords}
          onClose={handleCloseProgressDialog}
        />
      )}
    </Box>
  );
};

export default CptCodesGrid;
